/**
 * Doserne Academy — global "Coming Soon" screen used to hide
 * unfinished modules (Kurs, Sertifikater, Course viewer) in production
 * while they are being polished.
 */
import React from "react";
import { Link, Navigate } from "react-router-dom";
import { Loader2, Sparkles, ArrowLeft, Clock } from "lucide-react";
import { useDoserneAuth } from "./doserneAuth";
import { useLang } from "./i18n";
import DoserneShell from "./DoserneShell";

const COPY = {
  no: {
    eyebrow: "Snart tilgjengelig",
    title: "Denne modulen kommer snart",
    body: "Vi finpusser innholdet før vi lanserer det for alle. Kom tilbake om litt – vi gir beskjed når det er klart.",
    back: "Tilbake til hjem",
  },
  ro: {
    eyebrow: "În curând",
    title: "Această secțiune vine în curând",
    body: "Punem la punct ultimele detalii înainte de lansare. Revino curând – te anunțăm imediat ce este gata.",
    back: "Înapoi acasă",
  },
  en: {
    eyebrow: "Coming soon",
    title: "This module is on the way",
    body: "We are polishing the content before rolling it out. Check back soon — we will announce it as soon as it goes live.",
    back: "Back to home",
  },
};

function ComingSoonInner({ title, subtitle }) {
  const { lang } = useLang();
  const c = COPY[lang] || COPY.no;

  return (
    <DoserneShell>
      <div className="max-w-2xl mx-auto py-6" data-testid="coming-soon-page">
        <div className="relative overflow-hidden rounded-3xl border border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-orange-50 shadow-sm">
          {/* Decorative blobs */}
          <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-orange-200/50 blur-3xl" />
          <div className="absolute -bottom-12 -left-12 w-56 h-56 rounded-full bg-emerald-300/40 blur-3xl" />

          <div className="relative px-6 sm:px-10 py-12 text-center">
            <div className="inline-flex items-center gap-2 rounded-full border border-orange-300 bg-white/70 backdrop-blur px-3 py-1 mb-6">
              <Sparkles className="w-3.5 h-3.5 text-orange-500" />
              <span className="text-[10px] uppercase tracking-widest font-black text-orange-700">
                {c.eyebrow}
              </span>
            </div>

            <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-emerald-500 to-orange-500 flex items-center justify-center shadow-lg shadow-emerald-500/20">
              <Clock className="w-10 h-10 text-white" strokeWidth={2} />
            </div>

            <h1 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight mb-3">
              {title || c.title}
            </h1>
            <p className="text-base text-slate-600 leading-relaxed max-w-md mx-auto">
              {subtitle || c.body}
            </p>

            <Link
              to="/doserne"
              className="inline-flex items-center gap-2 mt-8 px-5 py-2.5 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 transition"
              data-testid="coming-soon-back"
            >
              <ArrowLeft className="w-4 h-4" />
              {c.back}
            </Link>
          </div>
        </div>
      </div>
    </DoserneShell>
  );
}

export default function ComingSoon({ title, subtitle }) {
  const { user, loading } = useDoserneAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#fafaf7]">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }
  if (!user) return <Navigate to="/doserne/logg-inn" replace />;
  return <ComingSoonInner title={title} subtitle={subtitle} />;
}
